import { fetchApiWithAuth } from "../api/fetch-api-with-auth.js";
import { clearBillingStatementsCache } from "./billing-statements.js";

function asStr(v) {
  return String(v ?? "").trim();
}

function parseJsonSafe(response) {
  return response.json().catch(() => null);
}

function parseApiFailure(json, status = 0) {
  const reason = asStr(json?.reason);
  if (reason) return reason;
  if (status === 401) return "unauthorized";
  return "request-failed";
}

function queryString(params = {}) {
  const p = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    p.set(key, String(value));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

async function bankRequest(
  path,
  { method = "GET", tenantId, body, query } = {},
) {
  const res = await fetchApiWithAuth(
    `/api${path}${queryString({ tenantId, ...query })}`,
    {
      method,
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    },
  );
  const json = await parseJsonSafe(res);
  if (!res.ok || json?.ok !== true) {
    return { ok: false, reason: parseApiFailure(json, res.status) };
  }
  return { ok: true, ...json };
}

export function fetchBankConnectionsRemote({ tenantId } = {}) {
  return bankRequest("/trainerstunden/bank/connections", { tenantId });
}

export function fetchBankSettingsRemote({ tenantId } = {}) {
  return bankRequest("/trainerstunden/bank/settings", { tenantId });
}

export function saveBankSettingsRemote({
  tenantId,
  autoSuggest,
  autoMatchMode,
} = {}) {
  return bankRequest("/trainerstunden/bank/settings", {
    method: "PUT",
    tenantId,
    body: { autoSuggest, autoMatchMode },
  });
}

export function startBankConnectRemote({
  tenantId,
  username,
  pin,
  provider,
  clientId,
  clientSecret,
  mode,
  useEnv,
  accountEmail,
} = {}) {
  return bankRequest("/trainerstunden/bank/connect/start", {
    method: "POST",
    tenantId,
    body: {
      username,
      pin,
      provider,
      clientId,
      clientSecret,
      mode,
      useEnv,
      accountEmail,
    },
  });
}

export function completeBankConnectRemote({
  tenantId,
  connectSessionId,
  tan,
} = {}) {
  return bankRequest("/trainerstunden/bank/connect/complete", {
    method: "POST",
    tenantId,
    body: { connectSessionId, tan },
  });
}

export function renewBankConnectionRemote({ tenantId, connectionId } = {}) {
  return bankRequest("/trainerstunden/bank/renew", {
    method: "POST",
    tenantId,
    body: { connectionId },
  });
}

export function patchBankAccountRemote({
  tenantId,
  accountId,
  useForReconciliation,
} = {}) {
  return bankRequest(
    `/trainerstunden/bank/accounts/${encodeURIComponent(asStr(accountId))}`,
    {
      method: "PATCH",
      tenantId,
      body: { useForReconciliation },
    },
  );
}

export function validateBankConnectionRemote({ tenantId, connectionId } = {}) {
  return bankRequest("/trainerstunden/bank/validate", {
    method: "POST",
    tenantId,
    body: { connectionId },
  });
}

export function disconnectBankRemote({ tenantId, connectionId } = {}) {
  return bankRequest("/trainerstunden/bank/disconnect", {
    method: "POST",
    tenantId,
    body: { connectionId },
  });
}

export function patchBankConnectionRemote({
  tenantId,
  connectionId,
  autoSync,
  accountEmail,
} = {}) {
  const body = {};
  if (typeof autoSync === "boolean") body.autoSync = autoSync;
  if (accountEmail != null) body.accountEmail = asStr(accountEmail);
  return bankRequest(
    `/trainerstunden/bank/connections/${encodeURIComponent(asStr(connectionId))}`,
    {
      method: "PATCH",
      tenantId,
      body,
    },
  );
}

export function syncBankConnectionRemote({ tenantId, connectionId } = {}) {
  return bankRequest("/trainerstunden/bank/sync", {
    method: "POST",
    tenantId,
    body: { connectionId },
  });
}

export function fetchBankReconciliationRemote({ tenantId, relevance } = {}) {
  return bankRequest("/trainerstunden/bank/reconciliation", {
    tenantId,
    query: relevance ? { relevance } : {},
  });
}

export function fetchBankRelevanceRemote({ tenantId } = {}) {
  return bankRequest("/trainerstunden/bank/relevance", { tenantId });
}

export function checkBankRelevanceRemote({ tenantId } = {}) {
  return bankRequest("/trainerstunden/bank/relevance/check", {
    method: "POST",
    tenantId,
  });
}

export function markBankRelevanceSeenRemote({ tenantId, transactionIds } = {}) {
  return bankRequest("/trainerstunden/bank/relevance/seen", {
    method: "POST",
    tenantId,
    body: { transactionIds },
  });
}

export function dismissBankRelevanceRemote({ tenantId, transactionId } = {}) {
  return bankRequest("/trainerstunden/bank/relevance/dismiss", {
    method: "POST",
    tenantId,
    body: { transactionId },
  });
}

export function saveBankTennisPartyRuleRemote({
  tenantId,
  counterpartyName,
  rule = "always_tennis",
} = {}) {
  return bankRequest("/trainerstunden/bank/relevance/party-rule", {
    method: "POST",
    tenantId,
    body: { counterpartyName, rule },
  });
}

export async function confirmBankMatchRemote({
  tenantId,
  transactionId,
  allocations,
} = {}) {
  const result = await bankRequest("/trainerstunden/bank/matches/confirm", {
    method: "POST",
    tenantId,
    body: { transactionId, allocations },
  });
  // Edge schreibt Payments/Statement-Status; Client-Cache sonst zeigt alte OFFEN-Werte.
  if (result?.ok) clearBillingStatementsCache();
  return result;
}

export function ignoreBankMatchRemote({
  tenantId,
  transactionId,
  statementId,
} = {}) {
  return bankRequest("/trainerstunden/bank/matches/ignore", {
    method: "POST",
    tenantId,
    body: { transactionId, statementId },
  });
}
